<template>
  <v-container>
    <!-- Filters Card -->
    <v-card class="mt-0">
      <v-card-title>
        <h4 class="subheading grey--text">Φίλτρα</h4>
        <v-spacer />
        <v-btn icon @click="showFilters = !showFilters">
          <v-icon>{{ showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-expand-transition>
        <div v-show="showFilters">
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="4">
                <v-combobox
                  v-model="filters.vehicles"
                  :items="vehicleOptions"
                  prepend-icon="mdi-truck"
                  label="Όχημα"
                  chips
                  multiple
                  clearable
                  @change="applyFilters"
                >
                  <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip
                      v-bind="attrs"
                      :input-value="selected"
                      close
                      @click="select"
                      @click:close="removeFromArray(filters.vehicles, item)"
                    >
                      <strong>{{ item }}</strong>&nbsp;
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>

              <v-col cols="12" sm="4">
                <v-combobox
                  v-model="filters.repairs"
                  :items="repairOptions"
                  label="Επισκευή"
                  prepend-icon="mdi-wrench-cog"
                  chips
                  multiple
                  clearable
                  @change="applyFilters"
                />
              </v-col>

              <v-col cols="12" sm="4">
                <v-menu
                  ref="fromMenu"
                  v-model="dateMenus.from"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateFromText"
                      label="Από Ημερομηνία"
                      prepend-icon="mdi-calendar-start"
                      readonly
                      clearable
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="clearDate('from')"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filters.date.from"
                    @change="dateMenus.from = false; applyFilters()"
                    :max="filters.date.to || undefined"
                    locale="el-GR"
                    scrollable
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" sm="4">
                <v-menu
                  ref="toMenu"
                  v-model="dateMenus.to"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateToText"
                      label="Έως Ημερομηνία"
                      prepend-icon="mdi-calendar-end"
                      readonly
                      clearable
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="clearDate('to')"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filters.date.to"
                    @change="dateMenus.to = false; applyFilters()"
                    :min="filters.date.from || undefined"
                    locale="el-GR"
                    scrollable
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" sm="4">
                <v-select
                  v-model="filters.type"
                  :items="fileTypeOptions"
                  item-value="value"
                  item-text="text"
                  label="Τύπος αρχείου"
                  prepend-icon="mdi-file-cog"
                  clearable
                  @change="applyFilters"
                />
              </v-col>

              <v-col cols="12" sm="4">
                <v-combobox
                  v-model="filters.tags"
                  :items="tagOptions"
                  label="Ετικέτες"
                  prepend-icon="mdi-tag-multiple"
                  chips
                  multiple
                  clearable
                  @change="applyFilters"
                />
              </v-col>

              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="filters.q"
                  label="Αναζήτηση (όνομα αρχείου ή σημειώσεις)"
                  prepend-icon="mdi-magnify"
                  dense
                  clearable
                  hide-details
                  @input="applyFilters"
                />
              </v-col>

              <v-col cols="12" sm="4" class="d-flex align-center">
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn text v-bind="attrs" v-on="on" class="ml-2">
                      Ταξινόμηση: {{ sort.label }}
                      <v-icon right>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="option in sortOptions"
                      :key="option.value"
                      @click="setSort(option)"
                    >
                      <v-list-item-title>{{ option.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-spacer />
                <v-btn text color="primary" @click="resetFilters">
                  Επαναφορά
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Documents Grid -->
    <v-card class="mt-4" :loading="loading">
      <v-card-title>
        <h1 class="mr-2 subheading grey--text">Έγγραφα Πελάτη</h1>
        <v-spacer></v-spacer>

        <!-- Selected actions -->
        <div class="d-flex align-center">
          <div class="grey--text caption mr-3" v-if="!loading">
            {{ filteredItems.length }} από {{ items.length }} έγγραφα
            <span v-if="selectedCount"> • Επιλεγμένα: {{ selectedCount }}</span>
          </div>

          <v-btn
            small
            outlined
            color="primary"
            class="mr-2"
            :disabled="selectedCount === 0"
            @click="downloadSelected"
            title="Λήψη επιλεγμένων"
          >
            <v-icon left small>mdi-download-multiple</v-icon>
            Λήψη
          </v-btn>

          <v-btn
            small
            color="primary"
            :disabled="selectedCount === 0"
            @click="openEmailDialog"
            title="Αποστολή επιλεγμένων με email"
          >
            <v-icon left small>mdi-email-multiple</v-icon>
            Email
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-row dense>
              <v-col
                v-for="item in pagedItems"
                :key="item._localId"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card elevation="1" class="selectable-card">
                  <!-- Selection checkbox overlay -->
                  <div class="checkbox-overlay">
                    <v-checkbox
                      :input-value="isSelected(item)"
                      @change="toggleSelect(item)"
                      dense
                      hide-details
                      :off-icon="'mdi-checkbox-blank-outline'"
                      :on-icon="'mdi-checkbox-marked'"
                      :title="isSelected(item) ? 'Αποεπιλογή' : 'Επιλογή'"
                    />
                  </div>

                  <!-- Thumbnail (reused styling) -->
                  <v-sheet height="160" class="d-flex align-center justify-center" tile>
                    <template v-if="isImage(item)">
                      <v-img
                        lazy-src="require('@/assets/logo_fm_app_l.png')"
                        :src="item.thumbnail || item.previewUrl || item.downloadUrl"
                        :alt="item.filename"
                        contain
                        max-height="90%"
                      >
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="grey lighten-5" />
                          </v-row>
                        </template>
                      </v-img>
                    </template>

                    <template v-else-if="isPdf(item)">
                      <v-img
                        lazy-src="require('@/assets/logo_fm_app_l.png')"
                        :src="item.thumbnail || item.previewUrl || item.downloadUrl"
                        :alt="item.filename"
                        contain
                        max-height="90%"
                      >
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="grey lighten-5" />
                          </v-row>
                        </template>
                      </v-img>
                    </template>

                    <template v-else>
                      <v-icon x-large>mdi-file-document-outline</v-icon>
                    </template>
                  </v-sheet>

                  <!-- Title with tooltip for notes -->
                  <v-card-title class="subtitle-2">
                    <template v-if="item.notes">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            class="text-truncate"
                            style="max-width:220px"
                            v-bind="attrs"
                            v-on="on"
                          >
                            {{ item.filename }}
                          </div>
                        </template>
                        <span style="white-space: pre-wrap;">{{ item.notes }}</span>
                      </v-tooltip>
                    </template>
                    <template v-else>
                      <div class="text-truncate" style="max-width:220px">
                        {{ item.filename }}
                      </div>
                    </template>
                  </v-card-title>

                  <!-- Subtitle + Tags/Metadata -->
                  <v-card-subtitle>
                    <div class="grey--text text--darken-1 caption">
                      {{ item.fileType || 'file' }} • {{ formatDate(item.fetched_at) }}
                    </div>
                    <div class="d-flex align-center flex-wrap mt-1">
                      <v-chip
                        v-if="typeof item.tag === 'string' && item.tag"
                        small
                        label
                        class="mr-1"
                        @click="filterByTag(item.tag)"
                      >
                        {{ item.tag }}
                      </v-chip>

                      <v-chip
                        v-if="item.vehicle"
                        small
                        label
                        class="mr-1"
                        @click="filterByVehicle(item.vehicle)"
                      >
                        {{ item.vehicle.plate_number }}
                      </v-chip>

                      <v-chip
                        v-if="item.repairId"
                        small
                        label
                        class="mr-1"
                        color="blue lighten-4"
                        @click="filterByRepair(item.repairId)"
                      >
                        Επισκευή #{{ item.repairStr }}
                      </v-chip>
                    </div>
                  </v-card-subtitle>

                  <!-- Actions -->
                  <v-card-actions>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small icon v-bind="attrs" v-on="on" @click="previewItem(item)" :title="'Προεπισκόπηση ' + item.filename">
                          <v-icon>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>Προεπισκόπηση</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small icon v-bind="attrs" v-on="on" @click="downloadItem(item)" :title="'Λήψη ' + item.filename">
                          <v-icon>mdi-download</v-icon>
                        </v-btn>
                      </template>
                      <span>Λήψη</span>
                    </v-tooltip>

                    <v-spacer></v-spacer>

                    <v-menu bottom right>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                      </template>
                      <v-list>
                        <v-list-item @click="copyUrl(item)">
                          <v-list-item-title>Αντιγραφή URL</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>

            <v-row justify="center" class="mt-4">
              <v-pagination v-model="page" :length="pageCount" circle></v-pagination>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Email Dialog (similar to CustomerInvoices.vue) -->
    <v-dialog v-model="attachDialog" max-width="900">
      <v-card>
        <v-card-title>
          <span class="headline">Αποστολή εγγράφων με Email</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="recipientEmail"
                label="Email παραλήπτη"
                prepend-icon="mdi-email"
                clearable
                type="email"
                :rules="[v => !!v || 'Απαιτείται email', v => /.+@.+\..+/.test(v) || 'Μη έγκυρο email']"
                class="mb-4"
              />
            </v-col>
            <v-col cols="12" sm="4" class="d-flex align-center">
              <v-switch
                v-model="draftEmail"
                label="Πρόχειρο;"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="messageBody"
                label="Μήνυμα"
                auto-grow
                rows="3"
                prepend-icon="mdi-text"
                clearable
              />
            </v-col>
          </v-row>

          <v-divider class="my-2" />

          <!-- Thumbnails of selected items -->
          <div class="grey--text text--darken-1 caption mb-2">
            Επιλεγμένα έγγραφα ({{ selectedCount }})
          </div>
          <v-row dense>
            <v-col
              v-for="doc in selectedItems"
              :key="'sel-'+doc._localId"
              cols="6" sm="3" md="2"
              class="d-flex flex-column align-center"
            >
              <v-hover v-slot="{ hover }">
                <v-card
                  :elevation="hover ? 6 : 2"
                  class="pa-1 selectable-thumb"
                  @click="openPreview(doc)"
                  title="Προεπισκόπηση"
                >
                  <v-img
                    v-if="isImage(doc) || doc.thumbnail"
                    :src="doc.thumbnail || doc.previewUrl || doc.downloadUrl"
                    :alt="doc.filename"
                    height="80"
                    contain
                  />
                  <div v-else class="d-flex align-center justify-center" style="height:80px">
                    <v-icon>mdi-file-document-outline</v-icon>
                  </div>
                </v-card>
              </v-hover>
              <div class="text-truncate caption mt-1" style="max-width:100%">
                {{ doc.filename }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="attachDialog = false">Ακύρωση</v-btn>
          <v-btn
            color="primary"
            :disabled="!canSend"
            @click="sendSelectedByEmail"
          >
            <v-icon left>mdi-send</v-icon>
            Αποστολή
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="900px" persistent>
      <v-card>
        <v-toolbar flat color="white">
          <span class="subtitle-1">
            Προεπισκόπηση
          </span>
          <v-spacer/>
          <v-btn icon @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-divider></v-divider>
        <v-card-text style="height:700px;">
          <template v-if="previewUrl">
            <img
              v-if="!previewIsPdf"
              :src="previewUrl"
              alt="preview"
              style="max-width:100%; max-height:100%; object-fit:contain;"
            />
            <embed
              v-else
              :src="previewUrl"
              type="application/pdf"
              width="100%"
              height="100%"
            />
          </template>
          <div v-else>Δεν υπάρχει διαθέσιμη προεπισκόπηση</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
/*
  Customer Documents Page
  - Fetches documents for a customer via streaming
  - Provides filters: vehicle, repair, date range, file type, tags, and full-text search (filename/notes)
  - Displays a responsive grid with thumbnails, tags, and quick actions
  - Adds selection per card, bulk download, and email dialog with preview
*/

import { authHeader } from '@/_helpers/auth-header.js'; // internal auth helper
import { streamItems } from '@/_helpers/stream.js'; // internal streaming utility

let localIdCounter = 0;

export default {
  name: 'CustomerDocuments',
  data() {
    return {
      loading: false,
      showFilters: true,

      // Raw items from API
      items: [],

      // Selection state: map of docId -> true
      selected: {},

      // Email dialog state
      attachDialog: false,
      recipientEmail: '',
      draftEmail: false,
      messageBody: '',

      // Preview dialog state
      previewDialog: false,
      previewUrl: null,
      previewItemRef: null,

      // Filtering and sorting
      filters: {
        q: '',
        type: null,
        tags: [],
        vehicles: [],
        repairs: [],
        date: {
          from: null, // 'YYYY-MM-DD'
          to: null,   // 'YYYY-MM-DD'
        },
      },
      dateMenus: {
        from: false,
        to: false,
      },
      sort: { value: '-date', label: 'Ημερομηνία (νεότερα πρώτα)' },
      sortOptions: [
        { value: '-date', label: 'Ημερομηνία (νεότερα πρώτα)' },
        { value: 'date', label: 'Ημερομηνία (παλαιότερα πρώτα)' },
        { value: 'name', label: 'Όνομα αρχείου (A-Z)' },
        { value: '-name', label: 'Όνομα αρχείου (Z-A)' },
        { value: 'type', label: 'Τύπος αρχείου' },
      ],
      fileTypeOptions: [
        { value: 'image', text: 'Εικόνα' },
        { value: 'pdf', text: 'PDF' },
        { value: 'file', text: 'Άλλο' },
      ],

      // Pagination
      page: 1,
      itemsPerPage: 12,
    };
  },
  computed: {
    // Derived options for filters
    vehicleOptions() {
      const set = new Set();
      for (const it of this.items) {
        if (it.vehicle && it.vehicle.plate_number) set.add(it.vehicle.plate_number);
      }
      return Array.from(set);
    },
    repairOptions() {
      const set = new Set();
      for (const it of this.items) {
        if (it.repairId) set.add(String(it.repairId));
      }
      return Array.from(set);
    },
    tagOptions() {
      const set = new Set();
      for (const it of this.items) {
        if (typeof it.tag === 'string' && it.tag) set.add(it.tag);
      }
      return Array.from(set);
    },

    // Date text fields
    dateFromText() {
      return this.filters.date.from ? this.formatDate(this.filters.date.from) : '';
    },
    dateToText() {
      return this.filters.date.to ? this.formatDate(this.filters.date.to) : '';
    },

    // Filtered, sorted, and paged data
    filteredItems() {
      const { q, type, tags, vehicles, repairs, date } = this.filters;
      const qLower = (q || '').toLowerCase();

      const inRange = (isoDate) => {
        if (!isoDate) return true;
        const d = new Date(isoDate);
        if (date.from) {
          const from = new Date(date.from + 'T00:00:00');
          if (d < from) return false;
        }
        if (date.to) {
          const to = new Date(date.to + 'T23:59:59');
          if (d > to) return false;
        }
        return true;
      };

      let res = this.items.filter((it) => {
        // type filter
        if (type && it.fileType !== type) return false;

        // tags filter (must include any of selected tags)
        if (tags && tags.length > 0) {
          const hasAny = tags.some((t) => t === it.tag);
          if (!hasAny) return false;
        }

        // vehicles filter
        if (vehicles && vehicles.length > 0) {
          if (!it.vehicle || !vehicles.includes(it.vehicle.plate_number)) return false;
        }

        // repairs filter
        if (repairs && repairs.length > 0) {
          const idStr = it.repairId != null ? String(it.repairId) : '';
          if (!repairs.includes(idStr)) return false;
        }

        // date range filter
        if (!inRange(it.fetched_at)) return false;

        // full-text search on filename and notes
        if (qLower) {
          const name = (it.filename || '').toLowerCase();
          const notes = (it.notes || '').toLowerCase();
          if (!name.includes(qLower) && !notes.includes(qLower)) return false;
        }

        return true;
      });

      // Sorting
      switch (this.sort.value) {
        case '-date':
          res.sort((a, b) => new Date(b.fetched_at) - new Date(a.fetched_at));
          break;
        case 'date':
          res.sort((a, b) => new Date(a.fetched_at) - new Date(b.fetched_at));
          break;
        case 'name':
          res.sort((a, b) => (a.filename || '').localeCompare(b.filename || ''));
          break;
        case '-name':
          res.sort((a, b) => (b.filename || '').localeCompare(a.filename || ''));
          break;
        case 'type':
          res.sort((a, b) => (a.fileType || '').localeCompare(b.fileType || ''));
          break;
        default:
          break;
      }

      return res;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage));
    },
    pagedItems() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredItems.slice(start, start + this.itemsPerPage);
    },

    // Selection derived
    selectedItems() {
      return this.items.filter((it) => this.selected[it.docId]);
    },
    selectedCount() {
      return this.selectedItems.length;
    },

    // Email dialog validation
    canSend() {
      const emailOk = this.recipientEmail && /.+@.+\..+/.test(this.recipientEmail);
      return this.selectedCount > 0 && emailOk && !this.loading;
    },

    // Preview helper
    previewIsPdf() {
      return this.previewItemRef ? this.isPdf(this.previewItemRef) : false;
    },
  },
  created() {
    this.fetchItems();
  },
  methods: {
    // API Fetch via streaming (referencing internal pattern)
    async fetchItems() {
      try {
        this.loading = true;
        const customerId = this.$route.params.id;

        this.items = [];
        const token = authHeader();
        const headers = token ? { Authorization: `${token}` } : {};

        const baseUrl = this.$api?.defaults?.baseURL || '';
        let url = `${baseUrl}/repairs/garage/customer/${customerId}/items`;

        // Append non-billing=true if current route path contains 'books'
        const currentPath = this.$route?.path || this.$route?.fullPath || '';
        if (typeof currentPath === 'string' && currentPath.includes('books')) {
          url += (url.includes('?') ? '&' : '?') + 'non_billing=true';
        }

        streamItems(
          url,
          { headers },
          (item) => {
            const base = {
              _localId: ++localIdCounter,
              filename: item.filename,
              downloadUrl: item.downloadUrl,
              uuid: item.uuid,
              metadataUrl: item.metadata,
              metadata: {},
              previewUrl: null,
              thumbnail: item.thumbnail,
              notes: item.notes,
              tag: item.tag,
              docId: item.id,
              fileType: this.inferFileType(item.filename),
              fetched_at: item.upload_at || item.created_at || new Date().toISOString(),

              // Attempt to normalize vehicle/repair fields for filtering
              vehicle:
                item.vehicle ||
                null,
              repairStr:
                item.repair_str ||
                null,
              repairId:
                item.repair_id ||
                item.repairid ||
                (item.repair && item.repair.id) ||
                null,
            };
            if (base.fileType === 'image') base.previewUrl = base.downloadUrl;
            this.$set(this.selected, base.docId, this.selected[base.docId] || false);
            this.items.push(base);
          },
          (err, ctx) => {
            console.error('stream error', err, ctx);
          }
        );
      } catch (err) {
        console.error('fetchItems error', err);
      } finally {
        // A small delay to allow streaming to fill some items before removing spinner
        setTimeout(() => (this.loading = false), 200);
      }
    },

    // Helpers
    inferFileType(filename = '') {
      const ext = filename.split('.').pop()?.toLowerCase();
      if (!ext) return 'file';
      if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'heic'].includes(ext)) return 'image';
      if (ext === 'pdf') return 'pdf';
      return 'file';
    },
    isImage(item) {
      return item.fileType === 'image';
    },
    isPdf(item) {
      return item.fileType === 'pdf';
    },
    formatDate(iso) {
      if (!iso) return '';
      // Support YYYY-MM-DD strings from date pickers as well
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const d = new Date(iso + 'T00:00:00');
        return new Intl.DateTimeFormat('el-GR', { dateStyle: 'medium' }).format(d);
      }
      const d = new Date(iso);
      if (isNaN(d)) return String(iso);
      return new Intl.DateTimeFormat('el-GR', { dateStyle: 'medium' }).format(d);
    },

    // Selection handlers
    isSelected(item) {
      return !!this.selected[item.docId];
    },
    toggleSelect(item) {
      const curr = !!this.selected[item.docId];
      this.$set(this.selected, item.docId, !curr);
    },

    // Filters and sorting
    applyFilters() {
      this.page = 1;
    },

    filterByTag(tag) {
      if (!this.filters.tags.includes(tag)) {
        this.filters.tags = [...this.filters.tags, tag];
      }
      this.applyFilters();
      // optionally open filters UI or scroll there if desired
    },

    filterByVehicle(item) {
      // If this plate is already in the filters, navigate to vehicle page
      if (this.filters.vehicles.includes(item.plate_number)) {
        // Adjust to your real route
        // Example using path:
        console.log('Navigating to vehicle id', item.id);
        // this.$router.push({ path: `/vehicles/details/${item.id}` });
        // or using named route:
        this.$router.push({ name: 'Vehicle', params: { vehicle: item.id } });
        return;
      }
  
      // Otherwise, add to filter
      this.filters.vehicles = [...this.filters.vehicles, item.plate_number];
      this.applyFilters();
    },
    filterByRepair(repairId) {
      const idStr = String(repairId);

      // If this repairId is already in the filters, navigate to repair page
      if (this.filters.repairs.includes(idStr)) {
        // Adjust to your real route
        // Example using path:
        // this.$router.push({ path: `/repairs/${encodeURIComponent(idStr)}` });
        // or using named route:
        this.$router.push({ name: 'edit-repair', params: { repairId: idStr } });
        return;
      }

      // Otherwise, add to filter
      this.filters.repairs = [...this.filters.repairs, idStr];
      this.applyFilters();
    },
   
    resetFilters() {
      this.filters = {
        q: '',
        type: null,
        tags: [],
        vehicles: [],
        repairs: [],
        date: { from: null, to: null },
      };
      this.page = 1;
    },
    setSort(option) {
      this.sort = option;
      this.applyFilters();
    },
    clearDate(which) {
      if (which === 'from') this.filters.date.from = null;
      if (which === 'to') this.filters.date.to = null;
      this.applyFilters();
    },
    removeFromArray(arr, val) {
      const idx = arr.indexOf(val);
      if (idx >= 0) arr.splice(idx, 1);
      this.applyFilters();
    },

    // Actions
    previewItem(item) {
      // Keep existing quick preview behavior (opens new tab)
      const url = item.previewUrl || item.downloadUrl;
      if (!url) return;
      window.open(url, '_blank');
    },
    openPreview(item) {
      const url = item.previewUrl || item.downloadUrl;
      if (!url) return;
      this.previewItemRef = item;
      this.previewUrl = url;
      this.previewDialog = true;
    },
    downloadItem(item) {
      if (!item.downloadUrl) return;
      const a = document.createElement('a');
      a.href = item.downloadUrl;
      a.download = item.filename || 'download';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    async copyUrl(item) {
      try {
        await navigator.clipboard.writeText(item.downloadUrl || '');
        this.$emit('notify', { type: 'success', message: 'Αντιγράφηκε το URL στο πρόχειρο' });
      } catch (e) {
        console.warn('Clipboard error', e);
      }
    },

    // Bulk actions
    async downloadSelected() {
      if (this.selectedCount === 0) return;
      // Trigger downloads one by one to avoid popup blockers
      for (const it of this.selectedItems) {
        this.downloadItem(it);
        // Small delay to ensure browser processes previous download
        await new Promise((r) => setTimeout(r, 120));
      }
    },
    openEmailDialog() {
      if (this.selectedCount === 0) return;
      this.attachDialog = true;
    },
    async sendSelectedByEmail() {
      // Compose FormData (similar pattern to CustomerInvoices)
      const formData = new FormData();
      formData.append('recipient_email', this.recipientEmail);
      if (this.draftEmail) formData.append('draft', 'true');
      if (this.messageBody) formData.append('message', this.messageBody);

      this.selectedItems.forEach((doc) => {
        formData.append('document_ids', doc.docId);
      });

      try {
        this.loading = true;
        // Adjust endpoint as needed on backend
        const res = await this.$api.post(
          '/repairs/send-documents-garage',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (res.data && res.data.status === 'ok') {
          alert('Τα έγγραφα εστάλησαν επιτυχώς!');
          this.attachDialog = false;
          this.messageBody = '';
          // keep selection in case user wants to reuse
        } else {
          alert('Κάτι πήγε στραβά στην αποστολή.');
        }
      } catch (e) {
        console.error(e);
        alert('Σφάλμα αποστολής.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Selection checkbox overlay on each card */
.selectable-card {
  position: relative;
}
.checkbox-overlay {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 2;
  background: rgba(255,255,255,0.9);
  border-radius: 4px;
}

/* Selected item thumb in dialog */
.selectable-thumb {
  width: 100%;
  cursor: pointer;
}
</style>